export type R2AudioCueKind = "TEACHER" | "CORRECT" | "WRONG" | "SILENCE" | "COMPLETE";

export interface R2BundledAudioAsset {
  readonly cueId: string;
  readonly kind: R2AudioCueKind;
  readonly publicUrl: string;
  readonly mime: "audio/mpeg" | "audio/wav" | "audio/ogg";
  readonly durationMs: number;
}

export type R2BundledAudioManifest = Readonly<Record<string, R2BundledAudioAsset>>;

export const R2_VOICE_SAMPLE_CUE_ID = "voice-sample:r2";

const AUDIO_ROOT = "/assets/audio/day1-r2/r2-v3-achernar";

const TEACHER_DURATIONS_MS = Object.freeze({
  "D1-01": 6216,
  "D1-02": 6456,
  "D1-03": 5328,
  "D1-04": 6072,
  "D1-05": 6000,
  "D1-06": 6624,
  "D1-07": 8424,
  "D1-08": 4992,
  "D1-09": 5184,
  "D1-10": 7536,
  "D1-11": 6000,
  "D1-12": 8760,
  "D1-13": 6072,
  "D1-14": 4656,
  "D1-15": 9816,
  "D1-16": 4704,
  "D1-17": 4104,
  "D1-18": 5400,
  "D1-19": 5640,
  "D1-20": 6216,
  "D1-21": 5640,
  "D1-22": 5880,
  "D1-23": 5904,
  "D1-24": 5496,
  "D1-25": 7440,
  "D1-26": 3480,
  "D1-27": 5976,
  "D1-28": 7536,
} satisfies Readonly<Record<string, number>>);

function bundledAsset(
  cueId: string,
  kind: R2AudioCueKind,
  fileName: string,
  durationMs: number,
): R2BundledAudioAsset {
  return Object.freeze({
    cueId,
    kind,
    publicUrl: `${AUDIO_ROOT}/${fileName}`,
    mime: "audio/mpeg",
    durationMs,
  });
}

const teacherAssets = Object.entries(TEACHER_DURATIONS_MS).map(([cardId, durationMs]) => {
  const cueId = teacherCueId(cardId);
  return [cueId, bundledAsset(cueId, "TEACHER", `${cardId}-teacher.mp3`, durationMs)] as const;
});

const feedbackAssets = Object.keys(TEACHER_DURATIONS_MS).flatMap((cardId) => [
  [feedbackCueId(cardId, "CORRECT"), bundledAsset(feedbackCueId(cardId, "CORRECT"), "CORRECT", "feedback-correct.mp3", 2808)],
  [feedbackCueId(cardId, "WRONG"), bundledAsset(feedbackCueId(cardId, "WRONG"), "WRONG", "feedback-wrong.mp3", 4656)],
  [feedbackCueId(cardId, "SILENCE"), bundledAsset(feedbackCueId(cardId, "SILENCE"), "SILENCE", "feedback-silence.mp3", 5496)],
] as const);

const completionAsset = bundledAsset("lesson:complete", "COMPLETE", "lesson-complete.mp3", 6000);
const voiceSampleAsset = bundledAsset(
  R2_VOICE_SAMPLE_CUE_ID,
  "TEACHER",
  "D1-01-teacher.mp3",
  TEACHER_DURATIONS_MS["D1-01"],
);

/** PL이 직접 승인한 Achernar 음성만 앱 내부 번들로 등록한다. */
export const R2_BUNDLED_AUDIO_MANIFEST: R2BundledAudioManifest = Object.freeze(Object.fromEntries([
  ...teacherAssets,
  ...feedbackAssets,
  [completionAsset.cueId, completionAsset],
  [voiceSampleAsset.cueId, voiceSampleAsset],
]));

export const R2_VOICE_GATE_STATUS = "GATE_B_PASS_ACHERNAR_BUNDLED" as const;

export interface AudioManifestIssue {
  readonly cueId: string;
  readonly reason: string;
}

export function validateR2AudioManifest(
  manifest: R2BundledAudioManifest,
): readonly AudioManifestIssue[] {
  const issues: AudioManifestIssue[] = [];
  for (const [cueId, asset] of Object.entries(manifest)) {
    if (cueId !== asset.cueId) {
      issues.push({ cueId, reason: "MANIFEST_KEY_CUE_ID_MISMATCH" });
    }
    if (!asset.publicUrl.startsWith("/assets/")) {
      issues.push({ cueId, reason: "AUDIO_PATH_OUTSIDE_R2_BUNDLE" });
    }
    if (!Number.isFinite(asset.durationMs) || asset.durationMs <= 0 || asset.durationMs > 20_000) {
      issues.push({ cueId, reason: "INVALID_AUDIO_DURATION" });
    }
    const extension = asset.publicUrl.split("?")[0]?.split(".").pop()?.toLowerCase();
    const expectedExtension = asset.mime === "audio/mpeg"
      ? "mp3"
      : asset.mime === "audio/wav"
        ? "wav"
        : "ogg";
    if (extension !== expectedExtension) {
      issues.push({ cueId, reason: "MIME_EXTENSION_MISMATCH" });
    }
  }
  return Object.freeze(issues);
}

export function teacherCueId(cardId: string): string {
  return `${cardId}:teacher`;
}

export function feedbackCueId(
  cardId: string,
  outcome: "CORRECT" | "WRONG" | "SILENCE",
): string {
  return `${cardId}:${outcome.toLowerCase()}`;
}

export const COMPLETION_CUE_ID = "lesson:complete";
